import { useState, useEffect, useCallback } from "react";

/**
 * Hook genérico para selección de location.
 * Carga la lista de locations vía la función pasada en `getLocations`
 * y persiste la selección en localStorage bajo `storageKey`.
 *
 * @param {Object} params
 * @param {Function} params.getLocations - Función async que devuelve el array de locations
 * @param {string} params.storageKey - Key de localStorage para persistir la selección
 * @param {string} [params.errorLabel] - Etiqueta opcional para console.error
 * @returns {{ locations: Array, selectedLocation: Object|null, setSelectedLocation: Function, loading: boolean }}
 */
export default function useLocationSelector({ getLocations, storageKey, errorLabel = "locations" }) {
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocationState] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function loadLocations() {
      try {
        const locs = await getLocations();
        if (cancelled) return;

        setLocations(locs);

        const savedKey = localStorage.getItem(storageKey);
        const saved = locs.find((l) => l.key === savedKey);
        setSelectedLocationState(saved || locs[0] || null);
      } catch (err) {
        console.error(`Error loading ${errorLabel}:`, err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadLocations();
    return () => { cancelled = true; };
  }, [getLocations, storageKey, errorLabel]);

  const setSelectedLocation = useCallback((location) => {
    setSelectedLocationState(location);
    if (location?.key) {
      localStorage.setItem(storageKey, location.key);
    }
  }, [storageKey]);

  return { locations, selectedLocation, setSelectedLocation, loading };
}
